import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { IdeasApiService } from '../../shared/api/ideas-api.service';
import { IdeaListItemDto } from '../../shared/api/ideas-api.types';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';
import { UserProfileApiService } from '../../shared/api/user-profile-api.service';

@Component({
  selector: 'app-my-ideas',
  standalone: true,
  imports: [CommonModule, RouterLink, LoaderComponent],
  templateUrl: './my-ideas.component.html',
  styleUrl: './my-ideas.component.css'
})
export class MyIdeasComponent implements OnInit, OnDestroy {
  ideas: IdeaListItemDto[] = [];
  loading = false;
  errorMessage = '';
  totalPoints = 0;
  page = 1;
  pageSize = 20;
  totalCount = 0;
  statusFilter: number | 'all' = 'all';

  readonly statusTabs: { value: number | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 0, label: 'Drafts' },
    { value: 1, label: 'Submitted' },
    { value: 3, label: 'Under Review' },
    { value: 2, label: 'Triage' },
    { value: 4, label: 'Accepted' },
    { value: 7, label: 'In Progress' },
    { value: 8, label: 'Delivered' }
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly api: IdeasApiService, private readonly profileApi: UserProfileApiService) {}

  ngOnInit(): void {
    this.loadMyIdeas();
    this.loadMyStats();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMyIdeas(): void {
    this.loading = true;
    this.errorMessage = '';
    const status = this.statusFilter === 'all' ? undefined : this.statusToApiStatus(this.statusFilter);
    this.api
      .getMyIdeas(this.page, this.pageSize, status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.ideas = result.items ?? [];
          this.totalCount = result.totalCount ?? 0;
          this.loading = false;
        },
        error: () => {
          this.errorMessage = 'Failed to load your ideas.';
          this.loading = false;
        }
      });
  }

  loadMyStats(): void {
    this.profileApi
      .getMyStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.totalPoints = stats?.totalPoints ?? 0;
        },
        error: () => {
          // Non-blocking: keep page functional.
          this.totalPoints = 0;
        }
      });
  }

  previousPage(): void {
    if (this.page <= 1) return;
    this.page--;
    this.loadMyIdeas();
  }

  nextPage(): void {
    if (this.page * this.pageSize >= this.totalCount) return;
    this.page++;
    this.loadMyIdeas();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get filteredIdeas(): IdeaListItemDto[] {
    return this.ideas;
  }

  get statusFilteredCount(): number {
    return this.ideas.length;
  }

  get inProgressCount(): number {
    return this.ideas.filter(i => i.status === 7).length;
  }

  get deliveredCount(): number {
    return this.ideas.filter(i => i.status === 8).length;
  }

  get pointsEarned(): number {
    return this.totalPoints;
  }

  setStatusFilter(value: number | 'all'): void {
    if (this.statusFilter === value) return;
    this.statusFilter = value;
    this.page = 1;
    this.loadMyIdeas();
  }

  private statusToApiStatus(status: number): string | undefined {
    const map: Record<number, string> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Triage',
      3: 'UnderReview',
      4: 'Accepted',
      5: 'Rejected',
      6: 'Planned',
      7: 'InProgress',
      8: 'Delivered',
      9: 'Archived',
      10: 'NeedMoreInfo'
    };
    return map[status];
  }

  getStatusLabel(status: number): string {
    const labels: Record<number, string> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Triage',
      3: 'Under Review',
      4: 'Accepted',
      5: 'Rejected',
      6: 'Planned',
      7: 'In Progress',
      8: 'Delivered',
      9: 'Archived',
      10: 'Need more info'
    };
    return labels[status] ?? 'Unknown';
  }

  getStatusClass(status: number): string {
    const classes: Record<number, string> = {
      0: 'tag-draft',
      1: 'tag-submitted',
      2: 'tag-triage',
      3: 'tag-under-review',
      4: 'tag-approved',
      5: 'tag-rejected',
      6: 'tag-planned',
      7: 'tag-in-progress',
      8: 'tag-delivered',
      9: 'tag-archived',
      10: 'tag-need-info'
    };
    return classes[status] ?? 'tag-default';
  }

  getUpvoteCount(idea: IdeaListItemDto): number {
    return Math.max(0, idea.upvoteCount ?? idea.voteCount ?? 0);
  }

  getDownvoteCount(idea: IdeaListItemDto): number {
    return Math.max(0, idea.downvoteCount ?? 0);
  }

  getCommentCount(idea: IdeaListItemDto): number {
    return Math.max(0, idea.commentCount ?? 0);
  }

  // Per-idea points depend on likes and status history; show totals instead.

  ideaRoute(idea: IdeaListItemDto): string[] {
    return this.isDraftStatus(idea.status) ? ['/submit-idea'] : ['/ideas', idea.id];
  }

  ideaQueryParams(idea: IdeaListItemDto): Record<string, string> | null {
    return this.isDraftStatus(idea.status) ? { ideaId: idea.id } : null;
  }

  private isDraftStatus(status: IdeaListItemDto['status']): boolean {
    if (typeof status === 'string') {
      return status.trim().toLowerCase() === 'draft';
    }
    return status === 0;
  }

  formatDate(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffSec < 60) return 'less than a minute ago';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `about ${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `about ${Math.floor(diffDays / 30)} months ago`;
    return `about ${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  }
}
